<script setup>
import { ref, computed, onMounted } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import axios from '@axios'
import { useUsersStore, useAppModeStore } from '@/stores'
import { canUseRestaurantAdminMode } from '@/utils/appModeEligibility'
import RaRestaurantCard from '@/layouts/components/RaRestaurantCard.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const router = useRouter()
const usersStore = useUsersStore()
const appModeStore = useAppModeStore()
const { myUser } = storeToRefs(usersStore)
const { mode } = storeToRefs(appModeStore)

const restaurants = ref([])
const loadingScope = ref(false)

const isEligible = computed(() => canUseRestaurantAdminMode(myUser.value))
const isPlatformAdmin = computed(() => myUser.value?.userLevel === 'admin')

const memberDisplayName = computed(() => {
  const u = myUser.value
  if (!u) return 'You'
  const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()

  return full || u.email || 'You'
})

const memberInitials = computed(() => {
  const u = myUser.value
  const first = u?.firstName?.[0] || ''
  const last = u?.lastName?.[0] || ''
  const initials = (first + last).toUpperCase()

  return initials || (u?.email?.[0] || '?').toUpperCase()
})

const memberSubtitle = computed(() => {
  const u = myUser.value
  if (!u) return ''
  if (u.title && u.company) return `${u.title} · ${u.company}`

  return u.company || u.title || ''
})

const memberMembershipLabel = computed(() => {
  const t = myUser.value?.membershipType
  if (!t || t === 'Standard') return ''

  return t === 'Trial' ? 'Platinum' : t
})

const sortedRestaurants = computed(() =>
  [...restaurants.value].sort((a, b) =>
    (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }),
  ),
)

const groupedRestaurants = computed(() => {
  const buckets = sortedRestaurants.value.reduce((groups, r) => {
    const key = r?.market || ''
    if (!groups[key]) groups[key] = []
    groups[key].push(r)

    return groups
  }, {})

  return Object.keys(buckets)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map(market => ({ market, restaurants: buckets[market] }))
})

async function loadScope() {
  if (!isEligible.value || loadingScope.value) return
  loadingScope.value = true
  try {
    const { data } = await axios.get('/user/me/restaurant-scope')
    restaurants.value = data.restaurants || []
  } catch {
    restaurants.value = []
  } finally {
    loadingScope.value = false
  }
}

onMounted(() => {
  if (isEligible.value) loadScope()
})

function close() {
  emit('update:modelValue', false)
}

function selectMode(newMode) {
  appModeStore.setMode(newMode, myUser.value?._id)
  close()
  if (newMode === 'member') {
    // RM platform admins switching back to their personal/member context land
    // directly on RM AI rather than their profile page.
    if (isPlatformAdmin.value) {
      router.push('/ai')

      return
    }
    const profileId = myUser.value?.profileId
    router.push(profileId ? `/m/${profileId}` : '/discover/')
  } else {
    router.push('/admin/profile')
  }
}

function handleKeydown(event) {
  if (event.key === 'Escape') close()
}
</script>

<template>
  <VNavigationDrawer
    :model-value="modelValue"
    location="bottom"
    temporary
    height="auto"
    class="mode-switcher-drawer"
    @update:model-value="emit('update:modelValue', $event)"
    @keydown="handleKeydown"
  >
    <div class="mode-drawer">
      <div class="mode-drawer__header">
        <span class="mode-drawer__title">App Mode</span>
        <VBtn
          icon
          variant="plain"
          size="small"
          aria-label="Close mode switcher"
          @click="close"
        >
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </div>

      <div class="mode-drawer__cards">
        <!-- Member mode card — shows the viewer's own identity -->
        <button
          class="mode-card"
          :class="{ 'mode-card--active': mode === 'member' }"
          type="button"
          aria-pressed="mode === 'member'"
          @click="selectMode('member')"
        >
          <div class="mode-card__avatar">
            <VImg
              v-if="myUser?.profileImageId"
              :src="mediaUrl(myUser.profileImageId, 'small')"
              class="mode-card__avatar-img"
              cover
            />
            <div
              v-else
              class="mode-card__avatar-fallback"
            >
              {{ memberInitials }}
            </div>
          </div>
          <div class="mode-card__body">
            <div class="mode-card__name">
              {{ memberDisplayName }}
              <span
                v-if="memberMembershipLabel"
                class="mode-card__name-tag"
              >
                ({{ memberMembershipLabel }})
              </span>
            </div>
            <div
              v-if="memberSubtitle"
              class="mode-card__desc"
            >
              {{ memberSubtitle }}
            </div>
          </div>
          <VIcon
            v-if="mode === 'member'"
            class="mode-card__check"
            color="primary"
            size="20"
          >
            mdi-check-circle
          </VIcon>
        </button>

        <!-- Restaurant Admin mode card -->
        <button
          class="mode-card"
          :class="{ 'mode-card--active': mode === 'restaurantAdmin' }"
          type="button"
          aria-pressed="mode === 'restaurantAdmin'"
          @click="selectMode('restaurantAdmin')"
        >
          <div class="mode-card__icon">
            <VIcon size="28">
              mdi-storefront-outline
            </VIcon>
          </div>
          <div class="mode-card__body">
            <div class="mode-card__name">
              Restaurant Account
            </div>
            <div class="mode-card__member">
              ({{ memberDisplayName }})
            </div>
            <div class="mode-card__desc">
              View bookings, fans & more for your restaurants
            </div>
          </div>
          <VIcon
            v-if="mode === 'restaurantAdmin'"
            class="mode-card__check"
            color="primary"
            size="20"
          >
            mdi-check-circle
          </VIcon>
        </button>
      </div>

      <!-- Restaurant gallery (only for non-platform admins) -->
      <div
        v-if="!isPlatformAdmin"
        class="mode-drawer__restaurants"
      >
        <div class="mode-drawer__section-header">
          <span class="mode-drawer__section-label">Your restaurants</span>
          <span
            v-if="!loadingScope && sortedRestaurants.length > 0"
            class="mode-drawer__section-count"
          >
            {{ sortedRestaurants.length }}
          </span>
        </div>
        <div
          v-if="loadingScope"
          class="mode-drawer__loading"
        >
          <VProgressCircular
            size="18"
            width="2"
            indeterminate
            color="primary"
          />
        </div>
        <div
          v-else-if="sortedRestaurants.length > 0"
          class="restaurant-list"
        >
          <template
            v-for="bucket in groupedRestaurants"
          >
            <div
              v-if="groupedRestaurants.length > 1 && bucket.market"
              :key="`market-${bucket.market}`"
              class="restaurant-list__market"
            >
              {{ bucket.market }}
            </div>
            <RaRestaurantCard
              v-for="r in bucket.restaurants"
              :key="r._id"
              :restaurant="r"
              :fallback-name="r.name"
            />
          </template>
        </div>
        <div
          v-else
          class="mode-drawer__empty"
        >
          No restaurants found.
        </div>
      </div>
      <div
        v-else
        class="mode-drawer__restaurants"
      >
        <div class="mode-drawer__section-header">
          <span class="mode-drawer__section-label">Your restaurants</span>
        </div>
        <div class="mode-drawer__empty">
          All restaurants (platform admin)
        </div>
      </div>
    </div>
  </VNavigationDrawer>
</template>

<style scoped>
.mode-switcher-drawer {
  max-height: 85vh;
  border-top-left-radius: var(--ds-radius-lg, 12px);
  border-top-right-radius: var(--ds-radius-lg, 12px);
  overflow: hidden;
}

.mode-drawer {
  display: flex;
  flex-direction: column;
  padding: var(--ds-space-4, 16px);
  padding-bottom: max(var(--ds-space-4, 16px), env(safe-area-inset-bottom));
  max-width: 640px;
  margin: 0 auto;
  width: 100%;
}

.mode-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--ds-space-4, 16px);
}

.mode-drawer__title {
  font-size: var(--ds-text-base, 16px);
  font-weight: 600;
  color: var(--ds-text-strong, inherit);
  letter-spacing: 0.01em;
}

.mode-drawer__cards {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-3, 12px);
  margin-bottom: var(--ds-space-5, 20px);
}

.mode-card {
  display: flex;
  align-items: flex-start;
  gap: var(--ds-space-3, 12px);
  padding: var(--ds-space-4, 16px);
  border: 1.5px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: var(--ds-radius-lg, 12px);
  background: rgb(var(--v-theme-surface));
  text-align: left;
  cursor: pointer;
  width: 100%;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.mode-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.5);
  background: rgba(var(--v-theme-primary), 0.04);
}

.mode-card--active {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.06);
}

.mode-card__icon {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  flex-shrink: 0;
  margin-top: 2px;
}

.mode-card--active .mode-card__icon {
  color: rgb(var(--v-theme-primary));
}

.mode-card__avatar {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--ds-brand-primary-light, rgba(var(--v-theme-primary), 0.12));
  display: flex;
  align-items: center;
  justify-content: center;
}

.mode-card__avatar-img {
  width: 100%;
  height: 100%;
}

.mode-card__avatar-fallback {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: rgb(var(--v-theme-primary));
}

.mode-card__body {
  flex: 1;
  min-width: 0;
}

.mode-card__kicker {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  margin-bottom: 2px;
}

.mode-card__name {
  font-size: var(--ds-text-base, 16px);
  font-weight: 600;
  color: var(--ds-text-strong, inherit);
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mode-card__name-tag {
  font-size: var(--ds-text-sm, 14px);
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  margin-left: 4px;
}

.mode-card__member {
  font-size: var(--ds-text-sm, 14px);
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  line-height: 1.45;
  white-space: normal;
  overflow-wrap: anywhere;
}

.mode-card__desc {
  font-size: var(--ds-text-sm, 14px);
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  line-height: 1.45;
  white-space: normal;
  overflow-wrap: anywhere;
}

.mode-card__check {
  flex-shrink: 0;
  margin-top: 2px;
}

.mode-drawer__restaurants {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-top: var(--ds-space-4, 16px);
  margin-inline: calc(-1 * var(--ds-space-4, 16px));
  padding-inline: 0;
}

.mode-drawer__section-header {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2, 8px);
  margin-bottom: var(--ds-space-3, 12px);
  padding-inline: var(--ds-space-4, 16px);
}

.mode-drawer__section-label {
  font-size: var(--ds-text-xs, 12px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.mode-drawer__section-count {
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
  padding: 3px 8px;
  border-radius: 999px;
  letter-spacing: 0;
  text-transform: none;
}

.restaurant-list {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-2, 8px);
  padding: var(--ds-space-2, 8px) var(--ds-space-4, 16px) var(--ds-space-3, 12px);
}

.restaurant-list__market {
  font-size: 12px;
  font-weight: 500;
  color: rgb(102, 102, 102);
  letter-spacing: 0.3px;
  margin-top: var(--ds-space-2, 8px);
}

.restaurant-list__market:first-child {
  margin-top: 0;
}

.mode-drawer__loading {
  display: flex;
  justify-content: center;
  padding: var(--ds-space-3, 12px) var(--ds-space-4, 16px);
}

.mode-drawer__empty {
  font-size: var(--ds-text-sm, 14px);
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-emphasis-opacity));
  padding: var(--ds-space-2, 8px) var(--ds-space-4, 16px);
}
</style>
